import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineEpics, createEpicMiddleware } from 'redux-observable-es6-compat';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { BrixxPlan, PlanData } from "sharedFeatures/sharedModels";
import { NewPlanDataValues } from 'sharedFeatures/sharedModels/newPlanDataValues';
import { PlanRenameResult } from "sharedFeatures/sharedModels/PlanRenameResult";
import { PlanTemplateDto } from 'sharedFeatures/sharedModels/planTemplateDto';
import { AccountsGroup } from 'sharedFeatures/sharedModels/xero/accountsGroupModel';
import { IEditPlanStartDate } from "sharedFeatures/sharedServices/Responses/BrixxFiles/IEditPlanStartDate";
import { ICopyPlanResponse, ICreatePlanResponse, IEditPlanResponse, IErrorResponse, IGetActivePlansCountResponse, IGetImageUrlResponse, IGetPlanNameResponse } from "sharedFeatures/sharedServices/Responses/plan.index";
import { DateUtils } from '../utils/date-utils';
import { DialogCreationService } from "sharedFeatures/sharedServices";
import { ICSVImportPlanResponse, isSuccessResponse } from './Responses';
import { ILoadPlanTemplatesResponse } from './Responses/BrixxFiles/ILoadPlanTemplatesResponse';

declare let handleAngularHttpError: any;

@Injectable()
export class PlanService {

    PlansPromise: Promise<Array<BrixxPlan>> | undefined;
    PlanReloadPromise: Promise<boolean> | undefined;
    ActivePlansPromise: Promise<IGetActivePlansCountResponse | IErrorResponse> | undefined;

    // Redux epic middleware for http requests
    private epicMiddleware = createEpicMiddleware();

    isPlansLoaded = new BehaviorSubject<boolean>(false);

    constructor(
        private http: HttpClient,
        private dialogCreation: DialogCreationService
    ) { }

    getPlans(companyId: string, choosenToKeep: boolean) {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            useChoosedToKeep: choosenToKeep.toString(),
            companyId: companyId
        };

        // Wrap http response with the custom promise to handle error
        this.PlansPromise = new Promise((resolve, reject) => {
            this.isPlansLoaded.next(false);
            const response = this.http.put<string>(window.appPath + 'BrixxFiles/LoadPlansData', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: Array<BrixxPlan> = [];

                    if (res && res.length) {
                        if (res.indexOf("error=sessiontimeout") > -1) {
                            window.gracePeriodActive = false;
                        }
                        else {
                            const parsedRes: { data: Array<BrixxPlan> } = JSON.parse(res);

                            if (parsedRes.data && parsedRes.data.length)
                                data = parsedRes.data;
                        }
                    }

                    console.log('data', data);
                    resolve(data);
                })
                .then(x => this.isPlansLoaded.next(true))
                .catch((error: HttpErrorResponse) => {
                    console.error('getPlans error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    editPlan(planData: PlanData): Promise<IEditPlanResponse | IErrorResponse> {

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        let nameObj = {
            planGUID: planData.planGUID,
            aPlanName: planData.planName,
            aPlanStart: planData.planStartDate.format('YYYY-MM-DDTHH:MM:SS'),
            aActualsPlanStart: planData.actualsPlanStartDate.format('YYYY-MM-DDTHH:MM:SS'),
            //aPlanStart: this.planData.planStartDate.toDate(), // Date will be converted to UTC YYYY-MM-DDTHH:MM:SS.000Z
            aDuration: planData.planDurationYears,
            aYearEnd: planData.planYearEnd,
            aCurrencyId: planData.planCurrencyId,
            aCurrencyPosition: planData.planCurrencyPosition,
            aXeroConnected: planData.xeroConnected,
            planTaxSystem: planData.planTaxSystem
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/EditPlan', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IEditPlanResponse | IErrorResponse = { success: false, errorMessage: '' };
                    if (res && res.length) {
                        data = JSON.parse(res);
                    }

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('EditPlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    editPlanStartDate(planData: PlanData, isRollForward: boolean) {

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        let nameObj = {
            planGUID: planData.planGUID,
            aPlanStart: planData.planStartDate.format('YYYY-MM-DDTHH:MM:SS'),
            aActualsStart: planData.actualsPlanStartDate.format('YYYY-MM-DDTHH:MM:SS'),
            isRollForward: isRollForward
        };

        // Wrap http response with the custom promise to handle error
        const response = this.http.post<string>(window.appPath + 'BrixxFiles/EditPlanStartDate', nameObj, { headers: headers });
        
        return lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | IEditPlanStartDate = { success: false, errorMessage: '' };
                    if (res && res.length) {
                        data = JSON.parse(res);
                    }

                    return data;
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('EditPlanStartDate error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    throw error;
                });
    }

    renamePlan(planGUID: string, aPlanName: string, signalRService: any): Promise<PlanRenameResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            planGUID: planGUID,
            aPlanName: aPlanName
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/EditPlanNameDirect', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: PlanRenameResult = new PlanRenameResult();
                    if (res && res.length) {
                        data = JSON.parse(res);
                    }

                    // Notify server about plan edited
                    signalRService.onPlanEdited(planGUID);

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('renamePlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    createPlan(
        aTemplateName: string,
        planName: string,
        planStart: string,
        actualsStart: string,
        duration: number,
        yearEndMonth: number,
        currencyId: number,
        aVatCalcOption: number,
        taxSystem: string,
        dataImportOptions: NewPlanDataValues | null,
        companyId: number,
        useCompanyData: boolean,
        planIsTour: boolean,
        plAccounts?: AccountsGroup[],
        bsAccounts?: AccountsGroup[]
    )
    {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            aTemplateName: aTemplateName,
            aPlanName: planName,
            aPlanStart: planStart,
            aActualsStart: actualsStart,
            aDuration: duration.toString(),
            aYearEnd: yearEndMonth?(yearEndMonth + 1).toString():0,
            aCurrencyId: currencyId.toString(),
            aCurrencyPosition: "0",
            aVatCalcOption: aVatCalcOption.toString(),
            aTaxSystem: taxSystem,
            aDataImportOptions: dataImportOptions,
            aCompanyId: companyId,
            aUseCompanyData: useCompanyData,
            aPlanIsTour: planIsTour,
            plAccounts: plAccounts,
            bsAccounts: bsAccounts
        };

        // Wrap http response with the custom promise to handle error
        return new Promise<IErrorResponse | ICreatePlanResponse>((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/NewPlan', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | ICreatePlanResponse = { success: false, errorMessage: '' };
                    if (res && res.length) {
                        this.resetActivePlansCount();
                        data = JSON.parse(res);
                    }
                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('createPlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

    }

    copyPlan(planguid: string, email: string, companyId: number = 0): Promise<ICopyPlanResponse | IErrorResponse> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            planGuid: planguid,
            email: email,
            companyId: companyId
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/CopyPlan', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: ICopyPlanResponse | IErrorResponse = { success: false, errorMessage: '' };
                    if (res && res.length) {
                        this.resetActivePlansCount();
                        data = JSON.parse(res);
                    }
                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('CopyPlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    removePlan(planguid: string, signalRService: any): Promise<ICopyPlanResponse | IErrorResponse> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            planGuid: planguid
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.put<string>(window.appPath + 'BrixxFiles/DeletePlan', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: ICopyPlanResponse | IErrorResponse = { success: false, errorMessage: '' };
                    if (res && res.length) {
                        this.resetActivePlansCount();
                        data = JSON.parse(res);
                    }
                    resolve(data);                    
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('RemovePlan error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    getImageUrl(planGuid: string): Promise<IGetImageUrlResponse | IErrorResponse> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            planGuid: planGuid,
        };

        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/GetPlanLogoUrl', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IGetImageUrlResponse | IErrorResponse = { success: false, errorMessage: '' };
                    if (res && res.length > 0)
                        data = JSON.parse(res);
                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetImageUrl error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    removePlanLogo(planguid: string, signalRService: any) {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            planGuid: planguid
        };

        // Wrap http response with the custom promise to handle error
        return new Promise<void>((resolve, reject) => {
            const response = this.http.put(window.appPath + 'BrixxFiles/DeletePlanLogo', nameObj, { headers: headers });
            lastValueFrom(response)
                .then(() => {
                    // Notify server about plan edited
                    signalRService.onPlanEdited(planguid);

                    resolve();
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('RemovePlanLogo error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    csvImportPlan(email: string, planName: string) {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let nameObj = {
            email: email,
            planName: planName
        };

        // Wrap http response with the custom promise to handle error
        return new Promise<ICSVImportPlanResponse | IErrorResponse>((resolve, reject) => {
            const response = this.http.put<string>(window.appPath + 'BrixxFiles/CsvImport', nameObj, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    // Notify server about plan edited
                    let data: ICSVImportPlanResponse | IErrorResponse = { success: false, errorMessage: '' };
                    if (res && res.length > 0)
                        data = JSON.parse(res);
                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('CsvImportPlan error', error);

                    handleAngularHttpError(error);
                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    GetPlanName(): Promise<IGetPlanNameResponse | IErrorResponse> {
        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.get<string>(window.appPath + 'BrixxFiles/GetPlanName');
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IGetPlanNameResponse | IErrorResponse = { success: false, errorMessage: '' };
                    if (res && res.length > 0)
                        data = JSON.parse(res);
                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetPlanName error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });
    }

    reloadPlanData(lastPlanGUID: string, versionID: number, userName: string): Promise<boolean> {

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let params = {
            planGuid: lastPlanGUID,
            versionID: versionID,
            userName: userName
        };

        // Wrap http response with the custom promise to handle error
        this.PlanReloadPromise = new Promise((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'BrixxFiles/ReloadPlan', params, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data = {success: false};
                    if (res && res.length) {
                        if (res.indexOf("error=sessiontimeout") > -1) { }
                        else
                            data = JSON.parse(res);
                    }

                    resolve(data.success);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('ReloadPlanData error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

        return this.PlanReloadPromise;
    }

    private resetActivePlansCount() { 
        this.ActivePlansPromise = undefined;
    }

    public GetActivePlansCount(customerId: number, companyId: number, isAllPlans: boolean = false): Promise<IGetActivePlansCountResponse | IErrorResponse> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let data = {
            customerId: customerId,
            companyId: companyId,
            isAllPlans: isAllPlans
        };

        // Wrap http response with the custom promise to handle error
        this.ActivePlansPromise = new Promise<IErrorResponse | IGetActivePlansCountResponse | IErrorResponse>((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Auth/GetActivePlansCount', data, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | IGetActivePlansCountResponse = { success: false, errorMessage: '' };

                    if (res && res.length) {
                        if (res.indexOf("error=sessiontimeout") === -1)
                            data = JSON.parse(res);
                    }

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetActivePlansCount error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

        return this.ActivePlansPromise;
    }

    public GetActivePlansCountByLicense(licenseId: number): Promise<IGetActivePlansCountResponse | IErrorResponse> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let data = {
            licenseId: licenseId
        };

        // Wrap http response with the custom promise to handle error
        let promise = new Promise<IErrorResponse | IGetActivePlansCountResponse | IErrorResponse>((resolve, reject) => {
            const response = this.http.post<string>(window.appPath + 'Auth/GetActivePlansCountByLicense', data, { headers: headers });
            lastValueFrom(response)
                .then((res: string) => {
                    let data: IErrorResponse | IGetActivePlansCountResponse = { success: false, errorMessage: '' };

                    if (res && res.length) {
                        if (res.indexOf("error=sessiontimeout") === -1)
                            data = JSON.parse(res);
                    }

                    resolve(data);
                })
                .catch((error: HttpErrorResponse) => {
                    console.error('GetActivePlansCountByLicense error', error);

                    handleAngularHttpError(error);

                    //if we need to transmit the error to a component
                    //reject(error);
                });
        });

        return promise;
    }

    // Return all middlewares of the plan service
    public getMiddlewares() {
        return [
            this.epicMiddleware
        ];
    }

    public runMiddlewares(): void {
        this.epicMiddleware.run(combineEpics(
        ));
    }

    // Raise fake server error
    public test500error() {
        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.get<string>(window.appPath + 'BrixxFiles/Test500error');
            lastValueFrom(response)
                .then((res: string) => {
                    const data = JSON.parse(res);
                });
        });
    }

    // We need fully restart Demo Tour - so reset isPlanTourActive flag on backend
    public resetDemoTour(): Promise<{ success: boolean }> {
        // Wrap http response with the custom promise to handle error
        return new Promise((resolve, reject) => {
            const response = this.http.get<string>(window.appPath + 'BrixxFiles/ResetDemoTour');
            lastValueFrom(response)
                .then((res: string) => {
                    const data = JSON.parse(res);
                });
        });
    }

    LoadPlanTemplates(): Promise<Array<PlanTemplateDto>> {
        const response = this.http.get<string>(`${window.appPath}BrixxFiles/GetPlanTemplates`, {  });
        return lastValueFrom(response)
            .then((res: string) => {
                let data = Array<PlanTemplateDto>();

                if (res && res.length) {
                    const parsedRes = JSON.parse(res) as ILoadPlanTemplatesResponse | IErrorResponse;
                    if (isSuccessResponse(parsedRes)) {
                        data = parsedRes.data;
                    }
                }

                return data;
            });
    }

    startDemoTour() {
        let d = new Date();
        let currentMonthIndex = d.getMonth();
        let currentYear = d.getFullYear();

        let startDate = new Date(currentYear, currentMonthIndex, 1, 12);
        let startDateParam = DateUtils.getStartDateParam(startDate);
        let actualsStartDateParam = startDateParam;

        let companyId = parseInt(localStorage.getItem('selectedCompanyId') ?? '');

        this.GetPlanName()
            .then((data: IGetPlanNameResponse | IErrorResponse) => {
                if (isSuccessResponse(data)) {
                    data = data as IGetPlanNameResponse;
                    let planName = data.planName!;
                    this.createPlan(
                        "bikeshop",
                        planName,
                        startDateParam,
                        actualsStartDateParam,
                        1,
                        12,
                        1,
                        0,
                        'vat',
                        null,
                        companyId,
                        false,
                        true)
                        .then((data: IErrorResponse | ICreatePlanResponse) => {
                            if (isSuccessResponse(data)) {
                                data = data as ICreatePlanResponse;
                                let url = encodeURI(data.redirectUrl);
                                console.log(url);
                                document.location.href = url;

                            } else {
                                data = data as IErrorResponse;
                                const popup = this.dialogCreation.createErrorWarning("Tour start error", data.errorMessage, () => { popup.close(); });
                                popup.show();
                            }
                        })
                } else {
                    const popup = this.dialogCreation.createErrorWarning("Tour start error", (<IErrorResponse>data).errorMessage, () => { popup.close(); });
                    popup.show();
                }
            }, (error) => {
                this.dialogCreation.createUnknownExceptionDialog(error).show();
            })
    }

}
